import { Box, Checkbox, Text } from '@factorialco/deprecated-design-system'

import Selector from 'modules/core/components/Selector'
import i18n from 'modules/core/lib/i18n'

import { LegalEntity } from 'modules/employees/hooks/useLegalEntities'
import type BulkEditForm from 'modules/employees/pages/EditInBulk/forms/BulkEdit'
import { EmployeeGroups } from 'modules/employees/pages/EditInBulk/types'

interface Props {
  legalEntities: LegalEntity[]
  bulkEditForm: BulkEditForm
  onChange: () => void
  onClick: () => void
  isChecked: boolean
  employeeGroups: EmployeeGroups[]
  width: number
}

const LegalEntityAndEmployeeGroupCell = ({
  legalEntities,
  bulkEditForm,
  isChecked,
  onChange,
  onClick,
  employeeGroups,
  width,
}: Props) => {
  const handleChangeLegalEntity = (id: unknown) => {
    bulkEditForm.setValues({
      legal_entity_id: id,
      employee_group_id: undefined,
    })
    onChange()
  }

  const handleChangeEmployeeGroup = (id: unknown) => {
    bulkEditForm.setValues({
      employee_group_id: id,
    })
    onChange()
  }

  const employeeGroupsByLegalEntity = employeeGroups.filter(
    (employeeGroup) => employeeGroup.legalEntityId === +bulkEditForm.get('legal_entity_id').value
  )

  const legalEntityOptions = legalEntities.map((legalEntity) => {
    return (
      <Selector.Option
        key={legalEntity.id}
        value={legalEntity.id}
        label={legalEntity.legalName}
        searchValue={legalEntity.legalName}
        isChecked={isChecked}
      >
        <Box width="full" overflowY="hidden">
          <Text.Normal size="200" weight="medium" ellipsis>
            {legalEntity.legalName}
          </Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  const employeeGroupOptions = employeeGroupsByLegalEntity.map((employeeGroup) => {
    return (
      <Selector.Option
        key={employeeGroup.id}
        value={employeeGroup.id}
        label={employeeGroup.name}
        searchValue={employeeGroup.name}
        isChecked={isChecked}
      >
        <Box width="full" overflowY="hidden">
          <Text.Normal size="200" weight="medium" ellipsis>
            {employeeGroup.name}
          </Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  const remainingWidth = width - 78
  const itemsToDisplay = legalEntities.length > 1 && employeeGroups.length > 1 ? 2 : 1
  const selectWidth = remainingWidth / itemsToDisplay

  return (
    <Box
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      height="full"
      paddingX="s16"
      paddingY="s12"
      border={{
        all: {
          width: 's2',
          style: 'solid',
          color: 'grey000White',
        },
      }}
      gap="s16"
      style={{ width: `${width}px` }}
    >
      <Box flexNoShrink>
        <Checkbox checked={isChecked} onChange={onClick} />
      </Box>
      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
        height="full"
        gap="s4"
      >
        {legalEntities.length > 1 && (
          <Box alignItems="center" style={{ width: `${selectWidth}px` }}>
            <Selector.Popover
              blankLabel={i18n.t('common.empty')}
              allSelectedLabel={i18n.t('common.all')}
              placeholder={i18n.t('common.select')}
              searchPlaceholder={i18n.t('common.search')}
              value={
                +bulkEditForm.get('legal_entity_id').value
                  ? [+bulkEditForm.get('legal_entity_id').value]
                  : []
              }
              onChange={handleChangeLegalEntity}
            >
              {legalEntityOptions}
            </Selector.Popover>
          </Box>
        )}
        {employeeGroups.length > 1 && (
          <Box alignItems="center" style={{ width: `${selectWidth}px` }}>
            <Selector.Popover
              blankLabel={i18n.t('common.empty')}
              placeholder={i18n.t('common.select')}
              searchPlaceholder={i18n.t('common.search')}
              value={
                +bulkEditForm.get('employee_group_id').value
                  ? [+bulkEditForm.get('employee_group_id').value]
                  : []
              }
              onChange={handleChangeEmployeeGroup}
            >
              {employeeGroupOptions}
            </Selector.Popover>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default LegalEntityAndEmployeeGroupCell
